import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api';
import { usePlayerBootstrap } from '../hooks/usePlayerBootstrap';
import { useInitialParamsSnapshot } from '../hooks/useInitialParams';
import { useTenant } from '../FeatureFlags';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function LandingPage() {
  const navigate = useNavigate();
  const { token } = useInitialParamsSnapshot();
  const { copy, flow } = useTenant();
  const { landingTitle, landingTitleHighlight, landingLede, landingLedeBold } =
    copy;
  const highlightIndex =
    landingTitleHighlight && landingTitle.endsWith(landingTitleHighlight)
      ? landingTitle.length - landingTitleHighlight.length
      : -1;
  const ledeBoldIndex =
    landingLedeBold && landingLede.includes(landingLedeBold)
      ? landingLede.indexOf(landingLedeBold)
      : -1;
  // Ario's embed is a fixed-width iframe, not a resizable browser viewport —
  // force the desktop grid layout regardless of the `lg` breakpoint.
  const forceDesktop = flow.chromeless;
  const [rewardEmail, setRewardEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const handleGetStarted = async () => {
    try {
      const capacity = await apiClient.checkCapacity();
      if (!capacity.hasCapacity) {
        navigate('/connect/waiting');
        return;
      }
      navigate('/connect');
    } catch (_error) {
      navigate('/connect/waiting');
    }
  };
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const value = rewardEmail.trim();
    if (!EMAIL_RE.test(value)) {
      setEmailError('Please enter a valid email address.');
      return;
    }
    setEmailError('');
    setSubmitting(true);
    try {
      await apiClient.captureRewardEmail(value);
      await handleGetStarted();
    } catch (_error) {
      setEmailError(
        'Something went wrong saving your email. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };
  usePlayerBootstrap();

  // Skip the button click for QuestionPro links (?token=), so the user needs
  // one less click. Tenants that require an email first still see the button.
  const hasAutoStarted = useRef(false);
  useEffect(() => {
    if (!token || flow.requireEmailCapture || hasAutoStarted.current) return;
    hasAutoStarted.current = true;
    handleGetStarted();
    // oxlint-disable-next-line react/exhaustive-deps
  }, [token, flow.requireEmailCapture]);

  // Renders either the plain "Get started" button (default) or the gift-card
  // email form (q11, flow.requireEmailCapture) — caller wraps with its own
  // visibility classes for the desktop/mobile breakpoint split.
  const renderCta = (variant: 'desktop' | 'mobile') => {
    const buttonClass = `inline-flex items-center justify-center gap-2.5 px-8 ${
      variant === 'desktop' ? 'py-4' : 'w-full py-3 sm:py-4'
    } bg-primary hover:bg-primary-deep text-white font-body font-semibold text-[16px] rounded-xl transition-colors duration-150 active:scale-[0.985] disabled:opacity-60`;

    if (!flow.requireEmailCapture) {
      return (
        <button
          data-testid={`landing-get-started-button-${variant}`}
          onClick={handleGetStarted}
          className={buttonClass}
        >
          Get started
          <svg
            className="w-[18px] h-[18px]"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>
      );
    }

    return (
      <form onSubmit={handleEmailSubmit} noValidate>
        <label
          className="block text-[15px] font-semibold text-ink mb-2.5"
          htmlFor={`reward-email-${variant}`}
        >
          {copy.rewardEmailLabel}
        </label>
        <div className="flex flex-col sm:flex-row gap-2.5 max-w-[460px]">
          <input
            id={`reward-email-${variant}`}
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="you@example.com"
            value={rewardEmail}
            onChange={(e) => {
              setRewardEmail(e.target.value);
              if (emailError) setEmailError('');
            }}
            className={`flex-1 min-w-0 px-4 py-3.5 rounded-xl border text-[15px] text-ink outline-none transition-colors focus:border-primary ${
              emailError ? 'border-red-600' : 'border-rule'
            }`}
          />
          <button
            data-testid={`landing-get-started-button-${variant}`}
            type="submit"
            disabled={submitting}
            className={buttonClass}
          >
            Start now
            <svg
              className="w-[18px] h-[18px]"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
              />
            </svg>
          </button>
        </div>
        {emailError && (
          <p className="text-[13px] text-red-600 mt-2">{emailError}</p>
        )}
      </form>
    );
  };

  return (
    <div
      className={`flex-1 flex flex-col pt-6 pb-4 sm:py-12 ${forceDesktop ? 'items-center justify-center px-5 sm:px-13' : 'px-5 sm:px-6 lg:items-center lg:justify-center'}`}
    >
      <div
        className={`w-full max-w-[1080px] gap-5 ${forceDesktop ? 'sm:gap-8 grid grid-cols-2 gap-20 items-center' : 'sm:gap-12 flex flex-col lg:grid lg:grid-cols-2 lg:gap-20 lg:items-center'}`}
      >
        {/* Left — hero */}
        <div>
          <h1
            className={`font-display font-bold text-ink leading-[1.15] tracking-[-0.03em] mb-2.5 sm:mb-4 ${forceDesktop ? 'text-[28px]' : 'text-[clamp(22px,6.5vw,48px)]'}`}
          >
            {highlightIndex >= 0 ? (
              <>
                {landingTitle.slice(0, highlightIndex)}
                <span className="text-primary">
                  {landingTitle.slice(highlightIndex)}
                </span>
              </>
            ) : (
              landingTitle
            )}
          </h1>
          <p className="text-[clamp(13px,3.6vw,15px)] sm:text-[16px] text-ink-muted leading-[1.45] sm:leading-[1.65] mb-0 sm:mb-10 max-w-[440px]">
            {ledeBoldIndex >= 0 ? (
              <>
                {landingLede.slice(0, ledeBoldIndex)}
                <strong className="text-ink-soft font-semibold">
                  {landingLede.slice(
                    ledeBoldIndex,
                    ledeBoldIndex + (landingLedeBold?.length ?? 0)
                  )}
                </strong>
                {landingLede.slice(
                  ledeBoldIndex + (landingLedeBold?.length ?? 0)
                )}
              </>
            ) : (
              landingLede
            )}
          </p>
          <div className={forceDesktop ? '' : 'hidden lg:block'}>
            {renderCta('desktop')}
          </div>
        </div>

        {/* Mobile CTA — shown above trust panel on small screens */}
        <div className={forceDesktop ? 'hidden' : 'lg:hidden'}>
          {renderCta('mobile')}
        </div>

        {/* Right — trust panel */}
        <div className="bg-paper rounded-2xl border border-rule-light p-5 sm:p-10">
          <p className="font-display text-[clamp(14px,3.5vw,15px)] sm:text-[15px] font-semibold text-ink-soft mb-4 sm:mb-7 tracking-[-0.01em]">
            Keep in mind
          </p>

          <div className="flex flex-col gap-4 sm:gap-6">
            <div className="flex items-start gap-3 sm:gap-4">
              <div
                className="w-8 h-8 sm:w-10 sm:h-10 rounded-[8px] sm:rounded-[10px] flex items-center justify-center flex-shrink-0"
                style={{ background: 'var(--retailer-primary-pale)' }}
              >
                <svg
                  className="w-4 h-4 sm:w-5 sm:h-5"
                  style={{ color: 'var(--retailer-primary)' }}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              </div>
              <div className="pt-0.5">
                <strong className="block text-[clamp(13px,3.4vw,14px)] sm:text-[14px] font-semibold text-ink-soft mb-0.5">
                  Less than 15 seconds
                </strong>
                <span className="text-[clamp(12px,3.1vw,13px)] sm:text-[13px] text-ink-muted leading-[1.4] sm:leading-[1.5]">
                  The connection process is very quick — it will take less than
                  15 seconds.
                </span>
              </div>
            </div>
            <div className="flex items-start gap-3 sm:gap-4">
              <div
                className="w-8 h-8 sm:w-10 sm:h-10 rounded-[8px] sm:rounded-[10px] flex items-center justify-center flex-shrink-0"
                style={{ background: 'var(--retailer-primary-pale)' }}
              >
                <svg
                  className="w-4 h-4 sm:w-5 sm:h-5"
                  style={{ color: 'var(--retailer-primary)' }}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"
                  />
                </svg>
              </div>
              <div className="pt-0.5">
                <strong className="block text-[clamp(13px,3.4vw,14px)] sm:text-[14px] font-semibold text-ink-soft mb-0.5">
                  One-time, secure connection
                </strong>
                <span className="text-[clamp(12px,3.1vw,13px)] sm:text-[13px] text-ink-muted leading-[1.4] sm:leading-[1.5]">
                  We will not track your purchases after this.
                </span>
              </div>
            </div>

            <div className="flex items-start gap-3 sm:gap-4">
              <div
                className="w-8 h-8 sm:w-10 sm:h-10 rounded-[8px] sm:rounded-[10px] flex items-center justify-center flex-shrink-0"
                style={{ background: 'var(--retailer-primary-pale)' }}
              >
                <svg
                  className="w-4 h-4 sm:w-5 sm:h-5"
                  style={{ color: 'var(--retailer-primary)' }}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
                  />
                </svg>
              </div>
              <div className="pt-0.5">
                <strong className="block text-[clamp(13px,3.4vw,14px)] sm:text-[14px] font-semibold text-ink-soft mb-0.5">
                  Anonymous & research-only
                </strong>
                <span className="text-[clamp(12px,3.1vw,13px)] sm:text-[13px] text-ink-muted leading-[1.4] sm:leading-[1.5]">
                  Your information will be kept anonymous, and only used for
                  research.
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
